import { isLossFrameDesign } from "./payoffFrame";

const STAGE1_UNRESOLVED_TASKS = new Set([
  "experiment_intro",
  "stage1_instructions",
  "stage1_task",
]);

const STAGE2_UNSTARTED_TASKS = new Set([
  "experiment_intro",
  "stage1_instructions",
  "stage1_task",
  "stage1_result",
  "stage2_setup",
]);

const TRACKING_GAME_TASKS = new Set([
  "stage1_task",
  "stage2_task",
]);

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function dollars(value) {
  return `$${finiteNumber(value).toFixed(2)}`;
}

export function stageStartingMoney(design, stageNumber) {
  return Math.max(0, finiteNumber(design?.[`stage${stageNumber}MaxPerParticipant`]));
}

export function stageTeamStartingMoney(design, stageNumber) {
  const playerCount = Math.max(1, finiteNumber(design?.playerCount, 2));
  return stageStartingMoney(design, stageNumber) * playerCount;
}

export function trackingMoneyLeft(player, design, stageNumber) {
  const startingMoney = stageStartingMoney(design, stageNumber);
  const dollarsPerSecond = Math.max(
    0,
    finiteNumber(design?.trackingDollarsPerSecond)
  );
  const result = player?.stage?.get?.(`stage${stageNumber}TrackingResult`) || {};
  const totalMs = Math.max(0, finiteNumber(result.totalMs));
  const insideMs = clamp(finiteNumber(result.insideMs), 0, totalMs);
  const lostDollars = ((totalMs - insideMs) / 1000) * dollarsPerSecond;

  return clamp(startingMoney - lostDollars, 0, startingMoney);
}

export function trackingMoneySavedFromResult(result, design) {
  const dollarsPerSecond = Math.max(
    0,
    finiteNumber(design?.trackingDollarsPerSecond)
  );
  const insideMs = Math.max(0, finiteNumber(result?.insideMs));

  return insideMs / 1000 * dollarsPerSecond;
}

function resolvedStage1Money(player, design, task) {
  if (STAGE1_UNRESOLVED_TASKS.has(task)) {
    return trackingMoneyLeft(player, design, 1);
  }

  const storedEarnings = Number(player?.get?.("stage1Earnings"));
  if (Number.isFinite(storedEarnings)) {
    return Math.max(0, storedEarnings);
  }

  return trackingMoneySavedFromResult(
    player?.stage?.get?.("stage1TrackingResult"),
    design
  );
}

function stage2TrackedMoney(player, design, game) {
  const stage2 = game?.get?.("stage2") || {};
  const byPlayerId = stage2.individualInCircleSecondsByPlayerId || {};
  const playerSeconds = finiteNumber(byPlayerId[player?.id], null);
  const teamSeconds = Math.max(0, finiteNumber(stage2.teamInCircleSeconds));
  const teamMoney = Math.max(
    0,
    finiteNumber(stage2.teamWinningsBeforeAdjustment)
  );

  if (playerSeconds !== null && teamSeconds > 0) {
    return teamMoney * (Math.max(0, playerSeconds) / teamSeconds);
  }

  return trackingMoneySavedFromResult(
    player?.stage?.get?.("stage2TrackingResult"),
    design
  );
}

function stage2SavedAtStake(game) {
  const stage2 = game?.get?.("stage2") || {};

  return Math.max(0, finiteNumber(stage2.teamWinningsBeforeAdjustment));
}

function resolvedStage2Money(player, design, task, game) {
  if (STAGE2_UNSTARTED_TASKS.has(task)) {
    return stageStartingMoney(design, 2);
  }

  if (task === "stage2_task") {
    return trackingMoneyLeft(player, design, 2);
  }

  const stage2 = game?.get?.("stage2") || {};
  const storedEarnings = Number(player?.get?.("stage2Earnings"));
  if (stage2.agreementStatus && Number.isFinite(storedEarnings)) {
    return Math.max(0, storedEarnings);
  }

  if (isLossFrameDesign(design)) {
    return stage2SavedAtStake(game);
  }

  return stage2TrackedMoney(player, design, game);
}

export function getLossFrameBannerInfo({ design, task, player, players, game }) {
  if (!task || !isLossFrameDesign(design)) {
    return null;
  }

  const partner = players?.find?.((candidate) => candidate.id !== player?.id) || null;
  const yourStage1Money = resolvedStage1Money(player, design, task);
  const partnerStage1Money = resolvedStage1Money(partner, design, task);
  const yourStage2Money = resolvedStage2Money(player, design, task, game);
  const partnerStage2Money = resolvedStage2Money(partner, design, task, game);

  return {
    you: yourStage1Money + yourStage2Money,
    partner: partnerStage1Money + partnerStage2Money,
    hidePartnerMoney: TRACKING_GAME_TASKS.has(task),
  };
}
